import { defineStore } from 'pinia';
import type { WalletState, WalletType, Balance } from '@/types/wallet';
import { getWalletService } from '@/common/services/getWalletService';
import { authService } from '@/common/services/authService';
import { getAssets, type Asset } from '@/api/assets';
import { getUsdtCnyRate } from '@/api/exchangeRate';
import { getReferralOverview } from '@/api/team';
import { useConfigStore } from '@/store/config';
import { TAKER_TOKEN_ADDRESS, USDT_CONTRACT_ADDRESS } from '@/common/config/env';
import logger from '@/common/utils/logger';

/** 进行中的验签登录 Promise，防止 challenge/sign 被并发重复触发 */
let loginInFlight: Promise<void> | null = null;
/** 用户主动点击连接时置 true；被动账户事件不得触发签名 */
let userLoginIntent = false;
/** 手机浏览器跳转钱包 App 后，页面可能被挂起/刷新；意图写入 sessionStorage 以跨回跳保留 */
const LOGIN_INTENT_KEY = 'panta_wallet_login_intent';
/** 登录弹窗在跳转期间应保持/恢复打开 */
const LOGIN_UI_PENDING_KEY = 'panta_login_modal_pending';
/** 跳转钱包授权常见需数分钟；过短会导致回跳后被当成被动重连而不验签 */
const LOGIN_INTENT_TTL_MS = 10 * 60 * 1000;
let mobileResumeBound = false;
let resumeInFlight: Promise<boolean> | null = null;
/** 扩展/DApp 当前账户与网站会话对齐（防 accountsChanged 丢失） */
let providerSyncInFlight: Promise<{ changed: boolean }> | null = null;
let lastProviderSyncAt = 0;
const PROVIDER_SYNC_GAP_MS = 800;
/** 避免 init 重复挂上地址/网络监听（堆积 MetaMask EventEmitter） */
let walletProviderEventsBound = false;
let onWalletAddressChange: ((address?: string) => void) | null = null;
let onWalletNetworkChange: ((chainId?: number) => void) | null = null;

const setUserLoginIntent = (on: boolean) => {
  userLoginIntent = on;
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (on) {
      sessionStorage.setItem(LOGIN_INTENT_KEY, String(Date.now()));
      sessionStorage.setItem(LOGIN_UI_PENDING_KEY, '1');
    } else {
      sessionStorage.removeItem(LOGIN_INTENT_KEY);
      sessionStorage.removeItem(LOGIN_UI_PENDING_KEY);
    }
  } catch {
    // ignore
  }
};

const hasUserLoginIntent = (): boolean => {
  if (userLoginIntent) return true;
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const raw = sessionStorage.getItem(LOGIN_INTENT_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > LOGIN_INTENT_TTL_MS) {
      sessionStorage.removeItem(LOGIN_INTENT_KEY);
      sessionStorage.removeItem(LOGIN_UI_PENDING_KEY);
      return false;
    }
    userLoginIntent = true;
    return true;
  } catch {
    return false;
  }
};

const isUserRejectedError = (err: unknown): boolean => {
  const msg = String((err as any)?.message || err || '');
  const code = String((err as any)?.code ?? '');
  return /reject|denied|cancel|user.?refus/i.test(msg) || code === '4001' || code === 'ACTION_REJECTED';
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const isLoginModalPending = (): boolean => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(LOGIN_UI_PENDING_KEY) === '1' && hasUserLoginIntent();
  } catch {
    return false;
  }
};

export const clearLoginModalPending = () => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(LOGIN_UI_PENDING_KEY);
    }
  } catch {
    // ignore
  }
};

// Define a more specific state type for this store
interface AxiWalletState extends WalletState {
  loginStage: 'idle' | 'network' | 'signature' | 'verify' | 'network_required';
  loginNetworkName: string;
  accessToken: string | null;
  authSyncing: boolean;
  assets: Asset[];
  totalBalance: string;
  usdtCnyRate: string;
  referrerCodeFromUrl: string;
  referrals: {
    total: number;
    valid_total: number;
    items: any[];
  };
  // 代币余额
  unxBalance: string;
  usdtBalance: string;
}

export const useWalletStore = defineStore('wallet', {
  state: (): AxiWalletState => ({
    isConnected: false,
    address: null,
    walletType: null, // This might become less relevant with Web3Modal
    chainId: null,
    balance: null,
    error: null,
    loginStage: 'idle',
    loginNetworkName: '',
    authSyncing: false,
    isConnecting: false,
    accessToken: null,
    assets: [],
    totalBalance: '0.0000',
    usdtCnyRate: '7.2',
    referrerCodeFromUrl: '',
    referrals: {
      total: 0,
      valid_total: 0,
      items: [],
    },
    // 代币余额
    unxBalance: '0',
    usdtBalance: '0',
  }),

  getters: {
    shortAddress: (state) => {
      if (!state.address) return '';
      return `${state.address.slice(0, 6)}...${state.address.slice(-4)}`;
    },
    // 网站会话独立于钱包连接；交易请求仍由 ensureAuthenticated 和后端校验。
    isAuthenticated: (state) =>
      !!state.address && !!state.accessToken,
    // 钱包是否已连接（可以进行链上操作）
    isWalletConnected: (state) => state.isConnected && !!state.address,
    currentWalletAddress: (state) => state.address,
  },

  actions: {
    /**
     * 仅用本地/刷新 token 恢复会话，绝不触发 challenge 或钱包签名。
     * @returns 是否已恢复为已登录
     */
    async restoreAuthenticatedSession(address: string): Promise<boolean> {
      if (!address) return false;

      const {
        setCurrentWalletAddress,
        getCurrentWalletAddress,
        getTokenForWallet,
        isTokenExpiredForWallet,
        clearTokenStorage,
        refreshSession,
      } = await import('@/common/services/request');
      setCurrentWalletAddress(address);
      const existing = getTokenForWallet(address);
      this.address = address;
      this.accessToken = existing || null;
      this.authSyncing = !existing || isTokenExpiredForWallet(address);

      // 包括过期 Token / 仅有 Cookie 的恢复；沿用统一续期、重试和钱包切换检查。
      const result = await refreshSession();
      const stillThisWallet =
        getCurrentWalletAddress() === address.toLowerCase();
      if (!stillThisWallet) {
        this.authSyncing = false;
        return false;
      }
      // Cookie 仍属旧账户 / 响应无 address：不得把旧 JWT 绑到新钱包
      if (result.reason === 'stale') {
        this.accessToken = null;
        this.authSyncing = false;
        return false;
      }
      if (!result.ok) {
        if (result.reason === 'transient' || result.reason === 'origin') {
          this.authSyncing = true;
          return false;
        }
        clearTokenStorage(address);
        this.accessToken = null;
        this.authSyncing = false;
        return false;
      }

      this.error = null;
      this.authSyncing = false;
      const configStore = useConfigStore();
      await configStore.loadDictionary().catch(() => false);
      await this.fetchUserAssets(true);
      this.refreshBalance();
      await this.fetchTokenBalances();
      logger.info('[WalletStore] Restored website session (no sign):', address);
      this.emitBindInviteCheck(600);
      return true;
    },

    /** 切到新钱包且无可用会话时打开登录底栏（非「登录已过期」熔断框） */
    promptLoginForCurrentWallet() {
      void import('@/common/services/request')
        .then(({ clearAuthGate }) => clearAuthGate())
        .catch(() => undefined);
      void import('@/store/loginUi')
        .then(({ useLoginUiStore }) => {
          useLoginUiStore().open();
        })
        .catch(() => undefined);
      try {
        uni.$emit('openLoginModal');
      } catch {
        /* ignore */
      }
    },

    /**
     * 用户主动登录：challenge → signMessage → verify。
     * 已有未过期 token 时跳过签名；并发调用复用同一 Promise。
     */
    async completeWalletLogin(address: string, options: { forceSign?: boolean } = {}) {
      if (!address) return;
      if (loginInFlight) {
        logger.info('[WalletStore] Login already in flight, reusing promise');
        return loginInFlight;
      }

      let loginSucceeded = false;
      loginInFlight = (async () => {
        const {
          setCurrentWalletAddress,
          saveToken,
          getTokenForWallet,
          isTokenExpiredForWallet,
        } = await import('@/common/services/request');
        const { walletChallenge, walletVerify, getAuthConfig } = await import('@/api/auth');
        setCurrentWalletAddress(address);

        // 有效 token 直接复用，避免重复弹签名
        if (!options.forceSign) {
          const existing = getTokenForWallet(address);
          if (existing && !isTokenExpiredForWallet(address)) {
            this.accessToken = existing;
            this.isConnected = true;
            this.address = address;
            this.isConnecting = false;
            this.error = null;
            const configStore = useConfigStore();
            await configStore.loadDictionary().catch(() => false);
            await this.fetchUserAssets(true);
            this.refreshBalance();
            await this.fetchTokenBalances();
            logger.info('[WalletStore] Reused valid token, skipped sign:', address);
            this.emitBindInviteCheck(600);
            loginSucceeded = true;
            return;
          }
        }

        // 登录前必须切到 auth/config 目标链（测试网 97 / 主网 56），失败则中止并提示用户手动切换
        let wantChainId: number | null = null;
        try {
          const authCfg = await getAuthConfig();
          if (authCfg?.chainId != null) {
            wantChainId = Number(authCfg.chainId);
          }
        } catch (e) {
          logger.warn('[WalletStore] getAuthConfig failed', e);
        }
        if (!hasUserLoginIntent()) return;
        const walletService = await getWalletService();
        this.loginNetworkName = walletService.getTargetChainName(wantChainId);
        this.loginStage = 'network';
        const okChain = await this.ensureCorrectChain(wantChainId, false);
        if (!hasUserLoginIntent()) return;
        if (!okChain) {
          this.loginStage = 'network_required';
          setUserLoginIntent(false);
          throw new Error('NETWORK_SWITCH_REQUIRED');
        }

        // Closing the login UI cancels acceptance, not the wallet's RPC Promise.
        if (!hasUserLoginIntent()) return;
        const challenge = await walletChallenge(address);
        if (!hasUserLoginIntent()) return;
        this.loginStage = 'signature';
        const { resolveWalletLoginMessage } = await import('@/common/utils/siwe');
        // SIWE（EIP-4361）：优先服务端已签发的 SIWE；否则用 challenge 字段拼装，签名与 verify 同一文案
        const loginMessage = resolveWalletLoginMessage(challenge);
        // 对齐 titan：注入/宿主钱包优先 personal_sign RPC，再回退 ethers signMessage
        const signature = await walletService.signPersonalMessage(
          address,
          loginMessage
        );
        if (!hasUserLoginIntent()) return;
        this.loginStage = 'verify';
        const tokenRes = await walletVerify({
          address,
          nonce: challenge.nonce,
          message: loginMessage,
          signature,
        });

        if (!hasUserLoginIntent()) return;
        saveToken(tokenRes.accessToken, tokenRes.expiresIn, address);
        this.accessToken = tokenRes.accessToken;
        this.isConnected = true;
        this.address = address;
        this.isConnecting = false;
        this.error = null;

        // 邀请码只预填到绑定弹窗，不在登录时静默 bind（需用户确认）
        try {
          const code =
            this.referrerCodeFromUrl ||
            localStorage.getItem('authCode') ||
            new URLSearchParams(window.location.search).get('ref') ||
            '';
          if (code && !this.referrerCodeFromUrl) {
            this.setReferrerCode(code);
          }
        } catch (e) {
          logger.warn('[WalletStore] referrer code sync skipped', e);
        }

        const configStore = useConfigStore();
        await configStore.loadDictionary();
        await this.fetchUserAssets(true);
        this.refreshBalance();
        await this.fetchTokenBalances();

        // 登录完成后检查绑定：未绑定才弹窗（必须已有 JWT）
        this.emitBindInviteCheck(700);

        logger.info('[WalletStore] Wallet login completed:', address);
        loginSucceeded = true;
        // 受理凭证由当前交易页 onShow / enterOrderTicketSurface 负责
      })()
        .catch((err) => {
          this.isConnecting = false;
          // 仅明确用户拒绝才清意图。切链失败 / 签名深链中断均保留，供回跳续登
          if (isUserRejectedError(err)) {
            setUserLoginIntent(false);
          } else if (hasUserLoginIntent()) {
            logger.warn('[WalletStore] Login interrupted, keep intent for wallet return:', err);
          }
          throw err;
        })
        .finally(() => {
          loginInFlight = null;
          if (loginSucceeded) {
            this.loginStage = 'idle';
            setUserLoginIntent(false);
          }
        });

      return loginInFlight;
    },

    /** 等待 WC / 注入钱包地址出现（回跳后会话恢复有延迟） */
    async waitForWalletAddress(maxMs = 10000): Promise<string | null> {
      const walletService = await getWalletService();
      const deadline = Date.now() + maxMs;
      let triedSilent = false;
      while (Date.now() < deadline) {
        let address = walletService.getAddress() || null;
        if (address) return address;
        if (!triedSilent && walletService.hasSavedSession()) {
          triedSilent = true;
          address = await walletService.attemptSilentReconnect();
          if (address) return address;
        }
        await sleep(400);
        address = walletService.getAddress() || null;
        if (address) return address;
      }
      return walletService.getAddress() || null;
    },

    /** 手机浏览器从钱包 App 回跳后：若仍有登录意图且已有地址，继续验签 */
    async resumePendingWalletLogin(reason = 'resume') {
      if (resumeInFlight) return resumeInFlight;

      resumeInFlight = (async () => {
        if (!hasUserLoginIntent()) return false;
        if (this.isAuthenticated) return false;

        // 回跳时刷新意图 TTL，避免长时间在钱包内授权后过期
        setUserLoginIntent(true);

        // Returning from a wallet does not cancel the pending personal_sign request.
        if (loginInFlight) {
          logger.info('[WalletStore] Login still in flight, skip resume:', reason);
          return false;
        }

        const address = await this.waitForWalletAddress(10000);
        if (!address) {
          logger.info('[WalletStore] Pending login but no address yet:', reason);
          return false;
        }

        logger.info('[WalletStore] Resume pending login after wallet return:', reason, address);
        this.isConnecting = true;
        try {
          await this.completeWalletLogin(address);
          return this.isAuthenticated;
        } catch (e) {
          logger.warn('[WalletStore] Resume login failed:', e);
          this.isConnecting = false;
          return false;
        }
      })().finally(() => {
        resumeInFlight = null;
      });

      return resumeInFlight;
    },

    /**
     * 主动对齐扩展 / DApp 当前选中账户与网站会话。
     * MetaMask 切号、钱包内置浏览器回前台时 accountsChanged 常丢失，需 eth_accounts 补一刀。
     */
    async syncProviderAccount(reason = 'sync'): Promise<{ changed: boolean }> {
      if (providerSyncInFlight) return providerSyncInFlight;
      const now = Date.now();
      if (now - lastProviderSyncAt < PROVIDER_SYNC_GAP_MS) {
        return { changed: false };
      }

      providerSyncInFlight = (async () => {
        lastProviderSyncAt = Date.now();
        try {
          const walletService = await getWalletService();
          const providerAddr = await walletService.readActiveAccount();
          const next = (providerAddr || '').toLowerCase().trim();
          const current = (this.address || '').toLowerCase().trim();

          if (!next) {
            // 仅断连状态：保留网站 JWT 会话（与被动空 accountsChanged 一致）
            if (this.isConnected) {
              await this.handleAccountsChanged(undefined);
              return { changed: true };
            }
            return { changed: false };
          }

          if (next === current && this.isConnected) {
            return { changed: false };
          }

          logger.info(
            '[WalletStore] Sync provider account',
            reason,
            current || '(none)',
            '->',
            next
          );
          await this.handleAccountsChanged(providerAddr || undefined);
          return { changed: true };
        } catch (e) {
          logger.warn('[WalletStore] syncProviderAccount failed:', reason, e);
          return { changed: false };
        } finally {
          providerSyncInFlight = null;
        }
      })();

      return providerSyncInFlight;
    },

    /** 监听回前台 / pageshow：补登录意图 + 对齐扩展/DApp 当前账户 */
    bindMobileLoginResume() {
      if (typeof document === 'undefined' || typeof window === 'undefined') return;
      if (mobileResumeBound) return;
      mobileResumeBound = true;

      const kick = (reason: string) => {
        if (document.visibilityState && document.visibilityState !== 'visible') return;
        void this.syncProviderAccount(reason);
        void this.resumePendingWalletLogin(reason);
        // WC / 注入钱包偶发晚于 focus，短延迟再对齐一次
        setTimeout(() => {
          void this.syncProviderAccount(`${reason}-retry`);
          void this.resumePendingWalletLogin(`${reason}-retry`);
        }, 1200);
      };

      document.addEventListener('visibilitychange', () => kick('visibility'));
      window.addEventListener('pageshow', () => kick('pageshow'));
      window.addEventListener('focus', () => kick('focus'));
    },

    async init() {
      // 页面刷新时重置 UI 连接中状态；登录意图从 sessionStorage 恢复（手机跳转钱包回跳）
      this.isConnecting = false;
      this.error = null;
      userLoginIntent = false;
      const pendingLogin = hasUserLoginIntent();

      // 等待页面可用即可，勿死等 window.load
      await new Promise<void>((resolve) => {
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          resolve();
          return;
        }
        const done = () => resolve();
        window.addEventListener('DOMContentLoaded', done, { once: true });
        window.addEventListener('load', done, { once: true });
        setTimeout(done, 3000);
      });

      const walletService = await getWalletService();
      await walletService.initialize();

      const {
        setCurrentWalletAddress,
        getCurrentWalletAddress,
      } = await import('@/common/services/request');

      // 刷新恢复顺序：
      // 1) 已有内存地址  2) 注入钱包 eth_accounts  3) WC 静默会话  4) 本地 JWT + axi_current_wallet
      let restoredAddress = walletService.getAddress() || null;

      if (!restoredAddress) {
        restoredAddress = await walletService.trySilentInjectedAccounts();
        if (restoredAddress) {
          logger.info('[WalletStore] Restored injected address via eth_accounts:', restoredAddress);
        }
      }

      if (!restoredAddress && walletService.hasSavedSession()) {
        logger.info('[WalletStore] Restoring WC address silently (no sign)...');
        restoredAddress = await walletService.attemptSilentReconnect();
      }

      this.isConnected = !!restoredAddress;

      if (!restoredAddress) {
        const saved = getCurrentWalletAddress();
        if (saved) {
          restoredAddress = saved;
          logger.info('[WalletStore] Restore saved website session (provider may attach later):', saved);
        }
      }

      logger.info(
        '[WalletStore] Init - address:',
        restoredAddress || '(none)',
        'pendingLogin:',
        pendingLogin
      );

      if (restoredAddress) {
        if (pendingLogin) {
          // 从钱包 App 深链返回：继续 challenge / 签名，不能只 restore token
          setUserLoginIntent(true);
          this.isConnecting = true;
          try {
            await this.completeWalletLogin(restoredAddress);
          } catch (e) {
            logger.warn('[WalletStore] Pending login on init failed, fallback restore:', e);
            this.isConnecting = false;
            await this.restoreAuthenticatedSession(restoredAddress);
          }
        } else {
          await this.restoreAuthenticatedSession(restoredAddress);
        }
      } else {
        this.isConnected = false;
        this.address = null;
        this.accessToken = null;
        setCurrentWalletAddress('');
        // 有登录意图但地址尚未恢复：继续轮询 WC 会话
        if (pendingLogin) {
          setUserLoginIntent(true);
          void this.pollWalletConnectThenLogin();
        }
      }

      // 订阅事件（只绑一次；重复 init 不得叠加监听）
      setTimeout(() => {
        if (walletProviderEventsBound) return;
        walletProviderEventsBound = true;
        onWalletAddressChange = (address?: string) => {
          void this.handleAccountsChanged(address);
        };
        onWalletNetworkChange = (chainId?: number) => {
          this.handleChainChanged(chainId);
        };
        void getWalletService().then((ws) => {
          ws.onAddressChange(onWalletAddressChange!);
          ws.onNetworkChange(onWalletNetworkChange!);
        });
      }, 100);

      this.bindMobileLoginResume();
      await this.fetchUsdtCnyRate();
    },

    setReferrerCode(code: string) {
      this.referrerCodeFromUrl = String(code || '').trim();
    },

    /** 清除邀请码/上级预填缓存（绑定结束后、退出登录时调用，避免脏数据覆盖默认上级） */
    clearReferrerCache() {
      this.referrerCodeFromUrl = '';
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('authCode');
        }
      } catch {
        // ignore
      }
      try {
        uni.removeStorageSync('authCode');
      } catch {
        // ignore
      }
    },

    /** 登录/恢复会话后通知绑定弹窗（仅已登录时；layout 内监听） */
    emitBindInviteCheck(delayMs = 500) {
      if (!this.isAuthenticated || !this.address) return;
      const code = this.referrerCodeFromUrl || '';
      try {
        setTimeout(() => {
          // 二次确认：延迟期间可能已登出
          if (!this.isAuthenticated || !this.address) return;
          uni.$emit('openBindInviteModal', { code, force: true });
        }, delayMs);
      } catch {
        // ignore
      }
    },

    async connect() {
      // Never issue a second challenge/signature while the wallet is still answering.
      if (loginInFlight) {
        return loginInFlight;
      }
      if (this.isConnecting) {
        logger.warn('[WalletStore] Resetting stuck isConnecting before reconnect');
        this.isConnecting = false;
      }

      // 解除先前 refresh 401 留下的门禁，否则后续资产请求会一直被挡
      try {
        const { clearAuthGate } = await import('@/common/services/request');
        clearAuthGate();
      } catch {
        // ignore
      }

      // 仅用户主动点击才允许验签（写入 sessionStorage，供手机跳转钱包回跳续登）
      setUserLoginIntent(true);
      this.loginStage = 'idle';
      this.error = null;

      try {
        const walletService = await getWalletService();
        // 检测是否在 OKX App 内浏览器中，如果是则直接连接
        if (walletService.isInOKXApp()) {
          logger.info('[WalletStore] In OKX App, using direct connection');
          await this.connectWithOKX();
          return;
        }

        // 仅宿主 WebView（AstralX）短等注入；外部手机浏览器不等，避免空耗
        if (walletService.isHostAppBrowser()) {
          await walletService.waitForInjectedProvider(2500);
        }

        // 有注入 → eth_requestAccounts（对齐 titan OKX 直连）；
        // 无注入（含 AstralX 仅壳浏览器）→ WC 深链 +「打开钱包 App」。
        const useInjected =
          walletService.hasInjectedProvider() &&
          !walletService.preferWalletConnectDeepLink();

        if (useInjected) {
          this.isConnecting = true;
          logger.info('[WalletStore] Injected wallet detected, eth_requestAccounts then login');
          try {
            const injectedAddress = await walletService.connectWithInjected();
            if (injectedAddress) {
              await this.completeWalletLogin(injectedAddress);
            } else {
              logger.info('[WalletStore] Injected connect returned no address');
              setUserLoginIntent(false);
            }
          } catch (err: any) {
            if (this.loginStage === 'network_required') throw err;
            const msg = String(err?.message || err || '');
            if (/reject|denied|cancel/i.test(msg)) {
              setUserLoginIntent(false);
              logger.info('[WalletStore] User rejected injected connection');
              throw err;
            }
            logger.warn('[WalletStore] Injected connect failed, falling back to modal:', err);
            setUserLoginIntent(true);
            this.isConnecting = true;
            await walletService.prepareAggregateConnect();
            await walletService.openConnectModal();
            void this.pollWalletConnectThenLogin();
            // 仅复位 UI；保留登录意图，等用户从钱包 App 返回后续签
            setTimeout(() => {
              if (this.isConnecting && !this.isAuthenticated) {
                logger.info('[WalletStore] UI connecting timeout (keep login intent for wallet return)');
                this.isConnecting = false;
              }
            }, 15000);
            return;
          } finally {
            if (!loginInFlight) {
              this.isConnecting = false;
            }
          }
          return;
        }

        const address = walletService.getAddress();
        // Reuse the existing session; a cancelled login must not open a new pairing.
        if (address) {
          this.isConnecting = true;
          try {
            await this.completeWalletLogin(address);
            if (this.isAuthenticated || !hasUserLoginIntent()) return;
          } catch (e) {
            if (!hasUserLoginIntent()) throw e;
            logger.warn('[WalletStore] Existing session login failed, fresh WC connect:', e);
          }
        }

        // 无可用会话时清掉上次 MetaMask 深链锁，强制打开聚合钱包列表
        this.isConnecting = true;
        logger.info('[WalletStore] Opening aggregate wallet modal');
        setUserLoginIntent(true);
        await walletService.prepareAggregateConnect();
        await walletService.openConnectModal();
        // 后台轮询：回跳后 subscribeProvider 偶发不触发，主动拉地址并验签
        void this.pollWalletConnectThenLogin();

        // 用户在钱包 App 内授权常超过 15s；切勿清掉 login intent，否则回跳后不会验签
        setTimeout(() => {
          if (this.isConnecting && !this.isAuthenticated) {
            logger.info('[WalletStore] UI connecting timeout (keep login intent for wallet return)');
            this.isConnecting = false;
          }
        }, 15000);
      } catch (error) {
        logger.error('[WalletStore] Connect error:', error);
        this.isConnecting = false;
        setUserLoginIntent(false);
        loginInFlight = null;
        throw error;
      }
    },

    /**
     * WC 深链登录：在登录意图有效期内轮询地址，连接成功后走 challenge/签名。
     * 覆盖「回跳后事件丢失」与「先连接、再签名两次跳转」场景。
     */
    async pollWalletConnectThenLogin() {
      const walletService = await getWalletService();
      const deadline = Date.now() + LOGIN_INTENT_TTL_MS;
      while (Date.now() < deadline) {
        if (!hasUserLoginIntent() || this.isAuthenticated) return;
        // 页面在后台时先等回前台，避免无意义请求
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          await sleep(800);
          continue;
        }
        if (loginInFlight) {
          await sleep(800);
          continue;
        }
        const address = walletService.getAddress() || null;
        if (address) {
          logger.info('[WalletStore] Poll found WC address, continue login:', address);
          this.isConnecting = true;
          try {
            await this.completeWalletLogin(address);
          } catch (e) {
            logger.warn('[WalletStore] Poll login attempt failed:', e);
            this.isConnecting = false;
            // 非用户拒绝则继续轮询（签名深链可能再次中断）
            if (isUserRejectedError(e)) return;
          }
          if (this.isAuthenticated) return;
        }
        await sleep(1000);
      }
      logger.info('[WalletStore] Poll wallet connect timed out');
    },

    /** 登录弹窗用户关闭时清掉卡死状态；超时不应轻易调用（会打断手机跳转回跳） */
    resetLoginAttempt() {
      setUserLoginIntent(false);
      this.isConnecting = false;
      this.loginStage = 'idle';
    },

    /**
     * 确保钱包已连接（用于需要链上操作的场景）
     * @returns true 钱包已连接，false 用户取消或连接失败
     */
    async ensureWalletConnected(): Promise<boolean> {
      // 如果钱包已连接，直接返回
      if (this.isConnected && this.address) {
        logger.info('[WalletStore] Wallet already connected');
        return true;
      }

      // 未连接，引导用户连接钱包登录
      logger.info('[WalletStore] Wallet not connected, need to connect first');
      try {
        await this.connect();
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.isConnected && !!this.address;
      } catch (error) {
        logger.error('[WalletStore] Failed to Connect:', error);
        return false;
      }
    },

    /**
     * 确保钱包在目标链上（仅在合约交互前调用）
     * @param chainId 可选：deposits/config.chainId；缺省用构建环境目标链
     * @returns true 链正确或切换成功，false 用户拒绝切换
     */
    async ensureCorrectChain(chainId?: number | null, notify = true): Promise<boolean> {
      const walletService = await getWalletService();
      const isAllowed = await walletService.checkAllowedChainAsync(chainId);
      if (isAllowed) {
        return true;
      }

      const targetName = walletService.getTargetChainName(chainId);

      // 直接尝试自动切换，钱包会弹出原生确认界面
      logger.info('[WalletStore] Wrong chain detected, requesting switch to', targetName);
      const switched = await walletService.switchToTargetChain(chainId);
      
      if (switched) {
        // 切换成功后等待链状态同步
        await new Promise(resolve => setTimeout(resolve, 1000));
        // 二次确认
        const confirmed = await walletService.checkAllowedChainAsync(chainId);
        if (confirmed) {
          return true;
        }
        logger.warn('[WalletStore] Chain switch reported success but verification failed');
      }

      // 切换失败（用户拒绝或钱包不支持），提示手动切换
      if (notify) {
        const { default: i18n } = await import('@/locales');
        uni.showToast({
          title: i18n.global.t('wallet.toasts.switchNetworkFailed', { chainName: targetName }),
          icon: 'none',
          duration: 3000,
        });
      }
      return false;
    },

    /**
     * 直接使用 OKX 钱包连接（绕过 Web3Modal）
     * 当 Web3Modal 无法正确检测 OKX 钱包时使用
     */
    async connectWithOKX() {
      if (this.isConnecting || loginInFlight) return;
      setUserLoginIntent(true);
      this.isConnecting = true;
      
      try {
        const walletService = await getWalletService();
        const address = await walletService.connectWithOKX();
        if (address) {
          this.walletType = 'OKX';
          await this.completeWalletLogin(address);
        } else {
          setUserLoginIntent(false);
        }
      } catch (error) {
        logger.error('[WalletStore] OKX connection failed:', error);
        this.error = error instanceof Error ? error.message : 'OKX 钱包连接失败';
        setUserLoginIntent(false);
      } finally {
        this.isConnecting = false;
      }
    },

    /**
     * OKX 钱包登录（兼容旧调用，等同于钱包直连登录）
     */
    async signInWithOKX(address: string) {
      await this.completeWalletLogin(address);
    },

    /**
     * 模拟模式演示登录（无需真实钱包，便于纯前端联调）
     */
    async demoLogin() {
      const { isMock } = await import('@/common/config/env');
      if (!isMock) {
        logger.warn('[WalletStore] demoLogin only available in mock mode');
        return;
      }
      const demoAddress = '0xDemoA1B2C3D4E5F60718293A4B5C6D7E8F901234';
      this.isConnecting = true;
      try {
        await this.completeWalletLogin(demoAddress);
        // 演示钱包无链上余额：注入虚拟钱包余额，支撑充值等交互
        this.unxBalance = '2000.00';
        this.usdtBalance = '5000.00';
        logger.info('[WalletStore] Demo login completed');
      } finally {
        this.isConnecting = false;
      }
    },

    async disconnect() {
      // 先清本地登录态，避免等 WalletConnect 断连/整页刷新前 UI 长时间卡住
      const serverLogout = authService.logout().then(() => true, () => false);
      this.clearReferrerCache();
      this.reset();

      try {
        const { discardOrderSubmissionTicket } = await import(
          '@/common/services/orderSubmissionTicket'
        );
        discardOrderSubmissionTicket();
      } catch {
        // ignore
      }
      try {
        const { usePositionStore } = await import('@/store/position');
        usePositionStore().clearPositions();
      } catch {
        // ignore
      }
      try {
        const { useAIPredictionPositionStore } = await import(
          '@/store/aiPredictionPosition'
        );
        useAIPredictionPositionStore().clearPositions();
      } catch {
        // ignore
      }
      try {
        const { useAnnouncementsStore } = await import('@/store/announcements');
        useAnnouncementsStore().clearSession();
      } catch {
        // ignore
      }

      try {
        const configStore = useConfigStore();
        configStore.clearStorage();
      } catch {
        // ignore
      }

      // 先同步清掉上次钱包深链/会话偏好，避免 reload 抢跑后仍锁死 MetaMask
      const walletService = await getWalletService();
      try {
        walletService.clearPersistedWalletChoice();
      } catch {
        // ignore
      }

      // WC/SDK 断连可能卡住数秒；限时后仍继续刷新，后台尽力清理
      await Promise.race([
        walletService.disconnect().catch(() => undefined),
        new Promise<void>((resolve) => setTimeout(resolve, 800)),
      ]);

      // disconnect 超时也再清一次，保证下次登录能进聚合钱包
      try {
        walletService.clearPersistedWalletChoice();
      } catch {
        // ignore
      }

      // Do not abort revocation by reloading while the request is in flight.
      if (!await serverLogout) {
        uni.showToast({ title: '本地已退出，服务器退出未确认，请检查网络', icon: 'none' });
        throw new Error('Server logout could not be confirmed');
      }
      // 先跳转到首页，再刷新页面彻底重置所有单例状态
      // uni.reLaunch 不会重新触发 onLaunch，所以需要 reload
      if (typeof window !== 'undefined') {
        window.location.hash = '#/pages/home/index';
        window.location.reload();
      }
    },

    /**
     * 钱包登录（兼容旧调用）：仅在用户主动流程中 challenge → 签名 → verify
     */
    async signIn(address: string) {
      if (!address) return;
      if (this.isConnecting || loginInFlight) return;
      setUserLoginIntent(true);
      this.isConnecting = true;
      try {
        await this.completeWalletLogin(address);
      } catch (error) {
        logger.error('[WalletStore] Sign-in failed:', error);
        setUserLoginIntent(false);
        throw error;
      } finally {
        this.isConnecting = false;
      }
    },

    async refreshBalance() {
      const walletService = await getWalletService();
      const provider = walletService.getEthersProvider();
      if (!provider || !this.address) return;

      try {
        const { ethers } = await import('ethers');
        const ethersProvider = new ethers.BrowserProvider(provider);
        const balanceWei = await ethersProvider.getBalance(this.address);
        const formattedBalance = parseFloat(ethers.formatEther(balanceWei)).toFixed(4);
        
        const network = await ethersProvider.getNetwork();
        const chainIdHex = '0x' + network.chainId.toString(16);
        let symbol = 'ETH';
        if (chainIdHex === '0x38' || chainIdHex === '0x61') symbol = 'BNB';
        else if (chainIdHex === '0x89') symbol = 'MATIC';

        this.balance = {
          native: balanceWei.toString(),
          formatted: formattedBalance,
          symbol: symbol,
        };
      } catch (error) {
        // Balance update failed silently
      }
    },

    /**
     * 获取 TAKER 和 USDT 代币余额
     * @param opts.chainId 可选：deposits/config.chainId，避免读错链
     * @param opts.usdtAddress 可选：deposits/config.token_address
     */
    async fetchTokenBalances(opts?: {
      chainId?: number | null;
      usdtAddress?: string;
    }) {
      // 需要钱包真正连接才能获取代币余额
      const walletService = await getWalletService();
      const walletAddress = walletService.getAddress();
      if (!walletAddress) {
        logger.info('[WalletStore] Cannot fetch token balances: wallet not connected');
        return;
      }

      try {
        const usdtAddr = String(opts?.usdtAddress || USDT_CONTRACT_ADDRESS).trim();
        const chainOpt =
          opts?.chainId != null && Number(opts.chainId) > 0
            ? { chainId: Number(opts.chainId) }
            : undefined;
        const [unxRes, usdtRes] = await Promise.all([
          walletService.getTokenBalance(TAKER_TOKEN_ADDRESS, chainOpt),
          walletService.getTokenBalance(usdtAddr, chainOpt),
        ]);
        
        const softRpcFail = (err: unknown) => {
          const msg = String(err ?? '');
          // 本地 H5 CORS / 空返回 0x 属环境问题，降噪；真异常仍 warn
          return /BAD_DATA|value=["']0x["']|Failed to fetch|CORS|ERR_FAILED/i.test(msg);
        };
        if (unxRes.success) {
          this.unxBalance = parseFloat(unxRes.data || '0').toFixed(2);
        } else if (softRpcFail(unxRes.error)) {
          logger.debug('[WalletStore] TAKER balance skipped (RPC/CORS):', unxRes.error);
        } else {
          logger.warn('[WalletStore] Failed to fetch TAKER balance:', unxRes.error);
        }
        if (usdtRes.success) {
          this.usdtBalance = parseFloat(usdtRes.data || '0').toFixed(2);
        } else if (softRpcFail(usdtRes.error)) {
          logger.debug('[WalletStore] USDT balance skipped (RPC/CORS):', usdtRes.error);
        } else {
          logger.warn('[WalletStore] Failed to fetch USDT balance:', usdtRes.error);
        }
        
        logger.info('[WalletStore] Token balances fetched - TAKER:', this.unxBalance, 'USDT:', this.usdtBalance);
      } catch (error) {
        logger.warn('[WalletStore] Failed to fetch token balances:', error);
      }
    },

    reset() {
      this.isConnected = false;
      this.address = null;
      this.walletType = null;
      this.chainId = null;
      this.balance = null;
      this.error = null;
      this.isConnecting = false;
      this.accessToken = null;
      this.assets = [];
      this.totalBalance = '0.0000';
      this.unxBalance = '0';
      this.usdtBalance = '0';
      // 与 clearReferrerCache 对齐，确保 persist 字段也被清空
      this.referrerCodeFromUrl = '';
    },

    async fetchUserAssets(force = false) {
      if (!this.isAuthenticated) return;
      const store = this as typeof this & {
        _assetsInFlight?: Promise<void> | null;
        _assetsAt?: number;
      };
      const ttlMs = 8_000;
      if (!force) {
        if (store._assetsInFlight) return store._assetsInFlight;
        if (store._assetsAt && Date.now() - store._assetsAt < ttlMs) return;
      }
      store._assetsInFlight = (async () => {
        try {
          const data = await getAssets();
          this.assets = data?.assets || [];
          this.totalBalance = data?.total_balance_usdt || '0.0000';
          store._assetsAt = Date.now();
        } catch (error) {
          this.assets = [];
          this.totalBalance = '0.0000';
        } finally {
          store._assetsInFlight = null;
        }
      })();
      return store._assetsInFlight;
    },

    async refreshToken() {
      try {
        const tokenResponse = await authService.refreshToken();
        this.accessToken = tokenResponse.accessToken || tokenResponse.access_token;
      } catch (error) {
        // 刷新失败时保持现有 token
      }
    },

    async fetchUsdtCnyRate() {
      if (!this.isAuthenticated) {
        return;
      }
      try {
        const data = await getUsdtCnyRate();
        this.usdtCnyRate = data?.rate || '7.2';
      } catch (error) {
        // Rate fetch failed silently
      }
    },

    async getReferrals(page = 1, pageSize = 20) {
      if (!this.isAuthenticated) return;
      try {
        const data = await getReferralOverview({
          page,
          page_size: pageSize,
          address: this.address || undefined,
        });
        this.referrals = {
          total: data.total ?? 0,
          valid_total: data.valid_total ?? data.total ?? 0,
          items: data.items || [],
        };
      } catch (error) {
        this.referrals = {
          total: 0,
          valid_total: 0,
          items: [],
        };
      }
    },

    async handleAccountsChanged(address?: string) {
      if (!address) {
        // 钱包锁定/后台断连不等于退出网站；只有用户主动退出才撤销服务器会话。
        this.isConnected = false;
        return;
      }

      this.isConnected = true;

      const addressChanged =
        !!this.address && address.toLowerCase() !== this.address.toLowerCase();

      // 仅用户主动连接流程才允许 challenge / 签名（含 sessionStorage 中的跳转钱包意图）
      if (hasUserLoginIntent() || this.isConnecting) {
        if (this.isConnecting) {
          this.isConnecting = false;
        }
        logger.info('[WalletStore] User-initiated account ready:', address);
        await this.completeWalletLogin(address);
        return;
      }

      // 被动事件：绝不弹签名。同地址且已登录则忽略；换地址则尝试恢复该地址本地/Cookie 会话
      if (!addressChanged && this.isAuthenticated) {
        return;
      }

      logger.info(
        '[WalletStore] Passive account event, restore token only (no sign):',
        address
      );
      const restored = await this.restoreAuthenticatedSession(address);
      // 切号后 Cookie 仍是旧账户 / 新地址无 JWT：引导用当前钱包重新登录，避免 401 风暴误报过期
      if (
        addressChanged &&
        !restored &&
        !this.isAuthenticated &&
        !this.authSyncing
      ) {
        this.promptLoginForCurrentWallet();
      }
    },

    handleChainChanged(chainId?: number) {
      if (chainId) {
        const chainHex = '0x' + chainId.toString(16);
        this.chainId = chainHex;
        logger.info('[WalletStore] Chain changed to:', chainHex);
        
        // 刷新余额（余额跟链相关）
        this.refreshBalance();
      }
    },
  },

  persist: {
    enabled: true,
    strategies: [
      {
        storage: {
          getItem: (key: string) => uni.getStorageSync(key),
          setItem: (key: string, value: any) => uni.setStorageSync(key, value),
          removeItem: (key: string) => uni.removeStorageSync(key),
        },
        paths: ['referrerCodeFromUrl'], // Only persist referrer code
      },
    ],
  } as any,
});
